import React from 'react';
import { usePatchQueueRequestMutation } from 'hooks/queue/';
import { useQueueContext } from '../../QueueContext';
import { prefixClass } from 'lib/utils';

import './QueuManualRequestButton.scss';
import { useQueryState } from 'hooks/useQueryState';
import { AskRequest, AskRequestStatuses } from '@hone-automation/common';
import { useEvent } from '../../../../../hooks/useEvent';

interface QueuManualRequestButtonProps {
  id: string;
  label: 'for me' | 'for ks' | 'for client';
}

export const QueueManualRequestButton: React.FC<QueuManualRequestButtonProps> = ({ id, label }) => {
  const {
    dispatch,
    state: { requests },
  } = useQueueContext();
  const [status] = useQueryState('status', AskRequestStatuses.In_Progress);
  const [, setRequestId] = useQueryState<string | null>('requestId');

  const { dispatch: emitEvent } = useEvent('onQueueRequestChangeStatus');
  const mutation = usePatchQueueRequestMutation(trx => {
    if (trx) {
      // filter request from queue
      const newRequestsByStatus = requests
        .filter(req => req.status === status && req.id !== trx.id)
        .sort((a, b) => {
          return new Date(a.updatedAt || a.createdAt).getTime() - new Date(b.updatedAt || b.createdAt).getTime();
        });
      // remove duplicates and filter out the current request
      const newRequests = [...new Set([...requests, ...newRequestsByStatus])].filter(req => req.id !== trx.id);
      // push the updated version of the current request
      newRequests.push(trx);
      // Update the requests in the context
      dispatch({ type: 'SET_REQUESTS', payload: newRequests });

      // Find the request to be selected (either the one we just updated or a different one based on status)
      const nextTrx = trx.status !== status ? newRequestsByStatus[0] : trx;
      dispatch({ type: 'SET_SELECTED_ITEM', payload: nextTrx as unknown as AskRequest });
      // Update the requestId in the URL
      setRequestId(nextTrx?.id);
      //validate animation depending on status
      emitEvent({ field: trx.status, duration: 5000 });
    }
  });

  const handleClick = () => {
    const newStateBasedOnLabel = label === 'for me' ? AskRequestStatuses.Open : AskRequestStatuses.In_Progress;
    mutation.mutate({ id, status: newStateBasedOnLabel });
  };

  const prefix = prefixClass('queue-manual-request-button');

  return (
    <div className={prefix('wrapper')}>
      <div className={prefix('wrapper__container')}>
        <button
          className={prefix('wrapper__container-button')}
          type="button"
          onClick={handleClick}
          disabled={mutation.isPending}
          data-testid="queue-manual-request-button"
        >
          {mutation.isPending ? 'Updating...' : `Move to ${label}`}
        </button>
      </div>
    </div>
  );
};

export default QueueManualRequestButton;
